import React, { useState, useEffect } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { Menu, X, LogOut, ChevronDown, User } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem, DropdownMenuSeparator } from "@/components/ui/dropdown-menu";

export default function Navbar({ onStart, onLogin }) {
  const { t } = useLang();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const inApp = Boolean(user) && pathname.startsWith("/app/");
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  const go = (id, to) => {
    setOpen(false);
    if (to) { navigate(to); return; }
    const el = document.getElementById(id);
    if (el) el.scrollIntoView({ behavior: "smooth" });
    else navigate("/#" + id);
  };

  const links = [
    { id: "solution", label: t("nav.product") },
    { id: "how", label: t("nav.how") },
    { id: "generator", label: t("nav.generator"), badge: "NEW" },
    { id: "stories", label: t("nav.stories"), to: "/testimonianze" },
    { id: "cci", label: t("nav.cci"), to: "/metodologia", accent: true },
    { id: "roadmap", label: t("nav.roadmap"), to: "/roadmap" },
    { id: "products", label: t("nav.family"), hideXl: true },
    { id: "pricing", label: t("nav.pricing") },
    { id: "faq", label: t("nav.about") },
  ];

  return (
    <header
      className={`fixed top-0 inset-x-0 z-50 cpenz-glass-dark text-white border-b transition-[border-color,box-shadow] duration-300 ${scrolled ? "border-white/10 shadow-[0_12px_40px_-24px_rgba(0,0,0,0.9)]" : "border-transparent"}`}
      data-testid="navbar"
    >
      <div className="max-w-7xl mx-auto px-5 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" data-testid="nav-logo" aria-label="cpenz" className="shrink-0 md:hidden font-bold tracking-[0.24em] text-white text-[21px] sm:text-[22px] leading-none">cpenz</Link>

        <div className="hidden md:flex items-center gap-3 shrink-0 ml-auto">
          {user ? (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button data-testid="nav-user-menu" className="inline-flex items-center gap-2 rounded-full border border-white/15 pl-1 pr-3 py-1 hover:bg-white/10 transition-colors">
                  <span className="w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold text-white" style={{ background: "linear-gradient(135deg,#7c3aed,#3b82f6)" }}>
                    {(user.name || user.email || "?").slice(0, 1).toUpperCase()}
                  </span>
                  <ChevronDown size={14} className="text-slate-400" />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="rounded-xl">
                <DropdownMenuItem data-testid="nav-profile" onClick={() => navigate("/app/profile")}>
                  <User size={15} className="mr-2" /> {t("appnav.profile")}
                </DropdownMenuItem>
                <DropdownMenuSeparator />
                <DropdownMenuItem data-testid="nav-logout" onClick={async () => { await logout(); navigate("/"); }}>
                  <LogOut size={15} className="mr-2" /> {t("nav.logout")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          ) : (
            <Button data-testid="nav-login" variant="ghost" className="rounded-full text-white hover:bg-white/10 hover:text-white" onClick={onLogin}>
              {t("nav.login")}
            </Button>
          )}
          {!user && <Button data-testid="nav-start" onClick={onStart} className="rounded-full bg-white hover:bg-white/90 text-[#0b0f1a] px-5 font-semibold">
            {t("nav.start")} →
          </Button>}
        </div>

        <button className="md:hidden p-2 text-white ml-auto" aria-label="Menu" aria-expanded={open} onClick={() => setOpen(!open)} data-testid="nav-mobile-toggle">
          {open ? <X size={22} /> : <Menu size={22} />}
        </button>
      </div>

      {open && (
        <div className="md:hidden bg-[#050609] border-t border-white/10 px-5 py-5 space-y-4 max-h-[80vh] overflow-y-auto" data-testid="nav-mobile-menu">
          {user && (<div className="grid grid-cols-2 gap-2 pb-3 border-b border-white/10">{[["home", t("appnav.home"), "/app/home"], ["ideas", t("appnav.ideas"), "/app/dashboard"], ["community", "Community", "/community"], ["market", "Market", "/market"], ["credits", t("credits.title"), "/app/credits"], ["profile", t("appnav.profile"), "/app/profile"]].map(([k, l, to]) => <button key={k} data-testid={`nav-mobile-app-${k}`} onClick={() => { setOpen(false); navigate(to); }} className="rounded-2xl bg-white/5 px-3 h-11 text-left text-sm font-semibold text-white hover:bg-white/10">{l}</button>)}</div>)}
          {links.map((l) => (
            <button key={l.id} onClick={() => go(l.id, l.to)} className={`flex items-center gap-2 text-lg font-medium ${l.accent ? "text-[#a78bfa] font-semibold" : "text-white"}`} data-testid={`nav-mobile-${l.id}`}>
              {l.label}
              {l.badge && <span className="rounded-full bg-[#7c3aed] px-2 py-[2px] text-[10px] font-bold tracking-wider text-white leading-none">{l.badge}</span>}
            </button>
          ))}
          <div className="flex items-center justify-end pt-2">
            {user ? (
              <div className="flex gap-2">
                <Button className="rounded-full text-white hover:bg-white/10 hover:text-white" variant="ghost" onClick={() => { setOpen(false); navigate("/app/dashboard"); }}>{t("nav.dashboard")}</Button>
                <Button data-testid="nav-mobile-logout" className="rounded-full text-white hover:bg-white/10 hover:text-white" variant="ghost" onClick={async () => { setOpen(false); await logout(); navigate("/"); }}>{t("nav.logout")}</Button>
              </div>
            ) : (
              <Button className="rounded-full text-white hover:bg-white/10 hover:text-white" variant="ghost" onClick={() => { setOpen(false); onLogin?.(); }}>{t("nav.login")}</Button>
            )}
          </div>
          <Button data-testid="nav-mobile-start" onClick={() => { setOpen(false); onStart?.(); }} className="w-full rounded-full bg-white text-[#0b0f1a] font-semibold">
            {t("nav.start")} →
          </Button>
        </div>
      )}
    </header>
  );
}
